import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { HomeIcon } from '@heroicons/react/24/outline';
import PageHero from '../components/ui/PageHero';
import { vaultApi, type PortalSnapshot } from '../api/vault';
import { getApiErrorMessage } from '../utils/errorHandling';
import { getSupportLinks } from '../config/support';

export default function Portal() {
  const [data, setData] = useState<PortalSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const snap = await vaultApi.portalMe();
      setData(snap);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load portal'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const support = getSupportLinks();
  const formatWhen = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  if (loading) {
    return <p className="p-8 text-slate-500">Loading portal…</p>;
  }

  return (
    <div className="space-y-6">
      <PageHero
        title="My BirdMan"
        subtitle="Instance link, vault backup catalog, and support — restores stay ops-controlled."
      />

      {!data ? (
        <p className="text-sm text-rose-600">Unable to load portal data.</p>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="glass-panel rounded-2xl border border-slate-200/60 p-5">
            <div className="mb-3 flex items-center gap-2">
              <HomeIcon className="h-5 w-5 text-sky-600" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">
                Your instance
              </h2>
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Name</dt>
                <dd className="font-medium text-slate-900">{data.instance.name}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Environment</dt>
                <dd className="font-medium text-slate-900">{data.instance.environment}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Plan</dt>
                <dd className="font-medium text-slate-900">{data.instance.plan}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Instance ID</dt>
                <dd>
                  <code className="text-xs text-slate-700">{data.instance.id}</code>
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Vault replication</dt>
                <dd className="font-medium text-slate-900">{data.backupReplication}</dd>
              </div>
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              {support.help && (
                <a href={support.help} target="_blank" rel="noreferrer" className="btn btn-secondary text-xs">
                  Open support
                </a>
              )}
              <Link to="/system-settings?tab=billing" className="btn btn-secondary text-xs">
                Manage subscription
              </Link>
              <a href="/profile" className="btn btn-secondary text-xs">
                My profile
              </a>
            </div>
          </div>

          <div className="glass-panel rounded-2xl border border-slate-200/60 p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-600">
              Vault backups (catalog)
            </h2>
            <p className="mb-3 text-xs text-slate-500">{data.note}</p>
            {data.latestVaultBackups.length === 0 ? (
              <p className="text-sm text-slate-500">
                No vault replicas yet. Admins enable backups; replication runs after each successful
                backup.
              </p>
            ) : (
              <ul className="space-y-2">
                {data.latestVaultBackups.map((b) => (
                  <li
                    key={b.backupId}
                    className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 text-xs"
                  >
                    <p className="font-semibold text-slate-800">{b.backupId}</p>
                    <p className="text-slate-500">
                      {formatWhen(b.takenAt)} · {b.status} · {b.sizeBytes} bytes
                    </p>
                    <p className="mt-1 truncate font-mono text-[10px] text-slate-400">
                      sha256:{b.checksumSha256}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {data.dualControlRestore && (
              <p className="mt-3 text-[11px] text-amber-800">
                Restore-to-new-host requires two administrators (dual control) or break-glass.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
